<template>
  <div>
    <div class="flex flex-col gap-4">
      <InputField name="title" :label="t('title')" :placeholder="t('noClickbaitPlease')" required />

      <TextareaField name="description" :label="t('description')" required />

      <FileUploadField name="file" :label="t('videoFile')" accept="video/*" required />
    </div>

    <div class="flex items-center justify-end gap-4">
      <CVButton variant="default" type="submit" :is-loading="isPending" @click="submit">
        {{ initialVideo ? t('save') : t('create') }}
      </CVButton>
      <CVButton type="button" variant="secondary" :disabled="isPending" @click="$emit('cancel')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import FileUploadField from '@/components/FileInputField.vue';
import InputField from '@/components/InputField.vue';
import TextareaField from '@/components/TextareaField.vue';
import { useUploadVideo } from '@/queries/use-upload';
import { useCreateVideo, useUpdateVideo } from '@/queries/use-videos';
import { extractFileNameFromFile } from '@/utils/extract-file-name';
import { type UpdateVideoDto, type Video } from '@carvia/ros-client-types';

const TRAINING_VIDEO_TYPE = 'TRAINING' as const satisfies Video['type'];
import { useForm } from 'vee-validate';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

const props = defineProps<{
  initialVideo?: Video;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const { mutateAsync: uploadVideo, isPending: isUploading } = useUploadVideo();
const { mutateAsync: createVideo, isPending: isCreating } = useCreateVideo();
const { mutateAsync: updateVideo, isPending: isUpdating } = useUpdateVideo();
const isPending = computed(() => isUploading.value || isCreating.value || isUpdating.value);

const { handleSubmit } = useForm<UpdateVideoDto & { file: File | undefined }>({
  initialValues: {
    title: props.initialVideo?.title,
    description: props.initialVideo?.description,
    fileKey: props.initialVideo?.fileKey,
    file: undefined,
  },
  validationSchema: yup.object({
    title: yup.string().required(),
    description: yup.string().required(),
    file: yup.mixed().when('fileKey', {
      is: (val: string) => !val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
});

const submit = handleSubmit(async (values) => {
  let fileKey: string | undefined = undefined;
  if (values.file) {
    fileKey = await upload(values.file);
  }
  if (props.initialVideo) {
    await updateVideo({
      id: props.initialVideo.id,
      data: {
        description: values.description!,
        title: values.title!,
        type: TRAINING_VIDEO_TYPE,
        fileKey,
      },
    });
    emit('saved');
  } else {
    if (!fileKey) return;
    await createVideo({
      description: values.description!,
      title: values.title!,
      type: TRAINING_VIDEO_TYPE,
      fileKey,
    });
    emit('saved');
  }
});

const upload = async (file: File) => {
  const { fullFileName } = extractFileNameFromFile(file);
  const { fileKey } = await uploadVideo({
    file: file,
    params: { originalFileName: fullFileName, type: TRAINING_VIDEO_TYPE },
  });
  return fileKey;
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Title",
    "type": "Type",
    "description": "Description",
    "videoFile": "Video File",
    "create": "Create",
    "save": "Save",
    "noClickbaitPlease": "Please no clickbait"
  },
  "de": {
    "title": "Titel",
    "type": "Typ",
    "description": "Beschreibung",
    "videoFile": "Videodatei",
    "create": "Erstellen",
    "save": "Speichern",
    "noClickbaitPlease": "Bitte kein Clickbait"
  },
  "es": {
    "title": "Título",
    "type": "Tipo",
    "description": "Descripción",
    "videoFile": "Archivo de vídeo",
    "create": "Crear",
    "save": "Guardar",
    "noClickbaitPlease": "Sin titulares sensacionalistas, por favor"
  }
}
</i18n>
